import { TICKS_PER_BAR } from '../shared/tempo';
import { TRAP_WEAR } from './traps';
import type { SetDefinition } from './types';

export const MAIN_STAGE: SetDefinition = {
  id: 'soiree-v0',
  name: 'Main stage',
  style: 'Psytrance full-on',
  decor: 'lake',
  trackIds: ['soiree-ouverture', 'brume-du-lac', 'grenouille-acide', 'goa-des-etoiles'],
  arena: { width: 1600, height: 1000 },
  core: { radius: 56, maxHp: 600 },
  startingTrap: 'caisson-de-basse',
  trapWear: TRAP_WEAR,
  carriers: { everyBars: 12, frequencyPerVolume: 0.1, first: 'caisson-de-basse' },
  bomb: { everyBars: 64, lifetimeBars: 16, radius: 20 },
  dice: { lifetimeBars: 16, radius: 16, speakerChance: 0.5, speakerThrow: 96 },
  levelCurve: { baseVibes: 10, vibesPerLevel: 16 },
  pickups: { lifetimeTicks: 8 * TICKS_PER_BAR, speed: 12 },
  vibeRain: { bars: 8, dropsPerBar: 4, amount: 0.25, radius: 300, flightBeats: 2 },
  weaponSlots: 3,
  reviveBars: 1,
  coreRepairPerBar: 85,
  perPlayer: { spawnMul: 1.1, enemyHpMul: 0.9 },
  endlessPerMinute: { hp: 1.1, damage: 1.25 },
  sidedWaves: { everyBars: 8, chance: 0.4, randomShare: 0.2 },
  speakers: [
    {
      id: 'dome-chill',
      name: 'Le Dôme chill',
      description: 'Une brume qui soigne',
      x: 800,
      y: 120,
      radius: 40,
      plugBars: 2,
      aura: { kind: 'mist', slowFactor: 1, healPerBar: 6, radius: 160 },
      unlocksWeaponId: 'assiettes-chinoises',
    },
    {
      id: 'foret',
      name: 'La Forêt',
      description: 'Ralentit les bad vibes',
      x: 1430,
      y: 500,
      radius: 40,
      plugBars: 2,
      aura: { kind: 'mist', slowFactor: 0.6, healPerBar: 0, radius: 160 },
      unlocksWeaponId: 'monocycle',
    },
    {
      id: 'sub',
      name: 'Le Sub',
      description: 'Une onde de choc par kick',
      x: 420,
      y: 500,
      radius: 40,
      plugBars: 2,
      aura: { kind: 'shockwave', damage: 8, radius: 140, knockback: 30 },
      unlocksWeaponId: 'totem',
    },
    {
      id: 'cercle-acid',
      name: 'Le Cercle acid',
      description: 'Attire et rend vulnérable',
      x: 800,
      y: 880,
      radius: 40,
      plugBars: 2,
      aura: { kind: 'lure', radius: 150, markedDamageMul: 1.5 },
      unlocksWeaponId: 'baton-du-diable',
    },
  ],
  tiers: [
    {
      buildupPhrases: 4,
      breakBars: 4,
      boss: { maxHp: 1320, damage: 25, vibesDrop: 40 },
      spawns: [
        { enemyId: 'random', everyBars: 4, count: 4, fromPhrase: 0 },
        { enemyId: 'desagreable', everyBars: 2, count: 2, fromPhrase: 0 },
        { enemyId: 'desagreable', everyBars: 2, count: 1, fromPhrase: 1 },
        { enemyId: 'meprisant', everyBars: 8, count: 1, fromPhrase: 1 },
        { enemyId: 'random', everyBars: 4, count: 2, fromPhrase: 2 },
        { enemyId: 'male-alpha', everyBars: 8, count: 1, fromPhrase: 2 },
        { enemyId: 'desagreable', everyBars: 1, count: 1, fromPhrase: 3 },
        { enemyId: 'collant', everyBars: 4, count: 1, fromPhrase: 3 },
      ],
      dropSpawns: [
        { enemyId: 'random', everyBars: 4, count: 7, fromPhrase: 0 },
        { enemyId: 'desagreable', everyBars: 1, count: 3, fromPhrase: 0 },
        { enemyId: 'meprisant', everyBars: 8, count: 1, fromPhrase: 0 },
        { enemyId: 'male-alpha', everyBars: 8, count: 1, fromPhrase: 0 },
        { enemyId: 'collant', everyBars: 4, count: 1, fromPhrase: 0 },
      ],
    },
    {
      buildupPhrases: 4,
      breakBars: 4,
      boss: { maxHp: 2640, damage: 35, vibesDrop: 60 },
      spawns: [
        { enemyId: 'desagreable', everyBars: 1, count: 2, fromPhrase: 0 },
        { enemyId: 'random', everyBars: 2, count: 4, fromPhrase: 0 },
        { enemyId: 'intolerant', everyBars: 8, count: 1, fromPhrase: 0 },
        { enemyId: 'arnaqueur', everyBars: 4, count: 1, fromPhrase: 0 },
        { enemyId: 'meprisant', everyBars: 4, count: 1, fromPhrase: 1 },
        { enemyId: 'male-alpha', everyBars: 8, count: 1, fromPhrase: 1 },
        { enemyId: 'fatigue', everyBars: 8, count: 1, fromPhrase: 1 },
        { enemyId: 'random', everyBars: 2, count: 4, fromPhrase: 2 },
        { enemyId: 'filmeur', everyBars: 8, count: 1, fromPhrase: 2 },
        { enemyId: 'bavard', everyBars: 8, count: 1, fromPhrase: 2 },
        { enemyId: 'desagreable', everyBars: 1, count: 2, fromPhrase: 3 },
        { enemyId: 'male-alpha', everyBars: 4, count: 1, fromPhrase: 3 },
        { enemyId: 'zombie', everyBars: 8, count: 1, fromPhrase: 3 },
      ],
      dropSpawns: [
        { enemyId: 'desagreable', everyBars: 1, count: 5, fromPhrase: 0 },
        { enemyId: 'random', everyBars: 2, count: 9, fromPhrase: 0 },
        { enemyId: 'intolerant', everyBars: 8, count: 1, fromPhrase: 0 },
        { enemyId: 'meprisant', everyBars: 4, count: 1, fromPhrase: 0 },
        { enemyId: 'male-alpha', everyBars: 4, count: 2, fromPhrase: 0 },
        { enemyId: 'filmeur', everyBars: 8, count: 1, fromPhrase: 0 },
        { enemyId: 'zombie', everyBars: 8, count: 1, fromPhrase: 0 },
      ],
      bystanderSpawns: [
        { bystanderId: 'festivalier-en-detresse', everyBars: 4, count: 1, fromPhrase: 0 },
      ],
    },
  ],
};
